import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 40, right: 20, bottom: 50, left: 50 };

const FIELDS = [
  { key: 'pest365', label: 'Pest365', color: '#2563eb' },
  { key: 'pest365Plus', label: 'Pest365+', color: '#8b5cf6' },
  { key: 'oneTime', label: 'One-Time', color: '#f59e0b' },
  { key: 'initialServiceVisit', label: 'Init. Visit', color: '#16a34a' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick', color: '#dc2626' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    userSelect: 'none',
  },
  legendSwatch: {
    width: '14px',
    height: '3px',
    borderRadius: '2px',
  },
  container: {
    overflowX: 'auto',
    minWidth: '600px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function DispersionChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hiddenFields, setHiddenFields] = useState(new Set());

  const toggleField = (key) => {
    setHiddenFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!data || !data.dates || !data.series || data.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const height = 300;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Parse dates for display
    const parseDate = (d) => new Date(d);
    const formatDate = d3.timeFormat('%b %d');

    // Collect all visible CV values to compute Y domain
    const allValues = [];
    FIELDS.forEach((field) => {
      if (hiddenFields.has(field.key)) return;
      const values = data.series[field.key];
      if (values) {
        values.forEach((v) => {
          if (v != null) allValues.push(v);
        });
      }
    });

    const yMax = allValues.length > 0 ? d3.max(allValues) : 0.1;
    const yPadding = yMax * 0.1 || 0.01;

    // Scales
    const x = d3.scalePoint()
      .domain(data.dates)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([0, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines - horizontal
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Grid lines - vertical
    g.selectAll('.grid-v')
      .data(data.dates)
      .enter()
      .append('line')
      .attr('class', 'grid-v')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDate(parseDate(d))))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickSize(0).tickFormat((d) => `${Math.round(d)}%`))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Line generator
    const lineGen = d3.line()
      .x((d, i) => x(data.dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    // Draw lines and dots for each visible field
    FIELDS.forEach((field) => {
      if (hiddenFields.has(field.key)) return;
      const values = data.series[field.key];
      if (!values) return;

      // Line
      g.append('path')
        .datum(values)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', field.color)
        .attr('stroke-width', 2);

      // Dots
      values.forEach((v, i) => {
        if (v == null) return;
        g.append('circle')
          .attr('cx', x(data.dates[i]))
          .attr('cy', y(v))
          .attr('r', 3)
          .attr('fill', field.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 1);
      });
    });

    // Invisible overlay rectangles for tooltip hover (one per date column)
    const colWidth = data.dates.length > 1 ? innerW / (data.dates.length - 1) : innerW;
    data.dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;
      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          const visibleEntries = [];
          FIELDS.forEach((field) => {
            if (hiddenFields.has(field.key)) return;
            const values = data.series[field.key];
            if (values && values[i] != null) {
              visibleEntries.push({
                label: field.label,
                color: field.color,
                value: values[i],
              });
            }
          });
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: formatDate(parseDate(date)),
            entries: visibleEntries,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, hiddenFields]);

  if (!data || !data.dates || !data.series || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Dispersion Over Time</div>
        <div style={styles.subtitle}>Coefficient of Variation -- lower = more uniform pricing</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No dispersion data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Dispersion Over Time</div>
      <div style={styles.subtitle}>Coefficient of Variation -- lower = more uniform pricing</div>
      <div style={styles.legend}>
        {FIELDS.map((field) => {
          const hidden = hiddenFields.has(field.key);
          return (
            <div
              key={field.key}
              style={styles.legendItem}
              onClick={() => toggleField(field.key)}
            >
              <span
                style={{
                  ...styles.legendSwatch,
                  background: hidden ? '#d1d5db' : field.color,
                }}
              />
              <span
                style={{
                  color: hidden ? '#d1d5db' : '#374151',
                  textDecoration: hidden ? 'line-through' : 'none',
                }}
              >
                {field.label}
              </span>
            </div>
          );
        })}
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.date}
          </div>
          {tooltip.entries.map((entry) => (
            <div
              key={entry.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: entry.color }}>{entry.label}</span>
              <span style={{ fontWeight: 600 }}>
                {entry.value.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
